import { monitoringExceptionHelper } from 'instrumentation/helpers';
import { useCallback, useState } from 'react';
import PluginService from 'services/PluginService';
import TeamMemberService from 'services/TeamMemberService';
import useModal from '__components/Modals/useModal';
import { MarketplacePlugin } from '../types';
import { useToast } from '__components/ToastProvider';
import { useTranslation } from 'react-i18next';
import { toastVariant } from '__components/ToastProvider';
import { useMarketplacePermissions } from '../permissions';
import { getKZNFileFlagsFromConfig } from '../components/Importer/usePluginKZNImporter';
import { performAssociationLink } from './association';
import { getHash, getProcessedAssistantConfig } from '@kizenapps/engine/util';
import {
  bareSecretName,
  createSaveSecret,
  getOwnedSecretNames,
} from './secrets';
import { useInvalidateIntegrationSecrets } from 'query/integration-secrets';

type UsePluginEnableConfig = {
  businessPluginAppsByApiName: Record<string, any>;
  refetch: (pluginApiName?: string) => Promise<unknown>;
};

export const processConfig = async (
  pluginApiName: string,
  currentAssistantConfig: any,
  businessPluginAppsByApiName: Record<string, any>,
  canEditIntegrationSecrets: boolean,
  userMode?: boolean
) => {
  const plugin = businessPluginAppsByApiName[pluginApiName];
  const setupAssistantConfig = userMode
    ? plugin?.plugin_app?.base_config?.user_setup_assistant
    : plugin?.plugin_app?.base_config?.setup_assistant;

  const businessPluginApp = businessPluginAppsByApiName[pluginApiName];

  let existingJSONConfig = businessPluginApp?.config;

  if (!existingJSONConfig) {
    existingJSONConfig = {};
  }

  const { partialNewConfig, actionsToLink } = await getProcessedAssistantConfig(
    currentAssistantConfig,
    setupAssistantConfig,
    {
      pluginApiName,
      saveSecret: createSaveSecret(pluginApiName, canEditIntegrationSecrets),
    }
  );

  const newConfig = {
    ...existingJSONConfig,
    ...partialNewConfig,
  };

  return { newConfig, actionsToLink };
};

const getEmptySecrets = async (
  pluginName: string,
  pluginApiName: string,
  ownedSecretNames: Set<string> = new Set()
) => {
  const { results } = await TeamMemberService.getIntegrationSecrets({
    search: pluginName,
    ordering: 'name',
    page: 1,
    page_size: 999,
  });

  const emptySecrets = results.filter(
    (secret: {
      api_name: string;
      obfuscated_value: string;
      business_plugin_app?: { plugin_app_api_name: string };
    }) =>
      !secret.obfuscated_value &&
      secret.business_plugin_app?.plugin_app_api_name === pluginApiName &&
      !ownedSecretNames.has(bareSecretName(pluginApiName, secret.api_name))
  );

  const hasEmptySecrets = emptySecrets.length > 0;

  return {
    emptySecrets,
    hasEmptySecrets,
  };
};

export const usePluginEnable = (config: UsePluginEnableConfig) => {
  const { businessPluginAppsByApiName, refetch } = config;

  const [pendingChange, setPendingChange] = useState({
    apiName: '',
    enabled: false,
  });
  const [setupAssistantPending, setSetupAssistantPending] = useState(false);
  const [setupAssistantConfig, setSetupAssistantConfig] = useState<any>(null);
  const [secretsToEdit, setSecretsToEdit] = useState<Array<any>>([]);
  const [noPermissionModalProps, , noPermissionModal] = useModal({
    handleSubmit: async () => {},
  });
  const [showToast] = useToast();
  const { t } = useTranslation();
  const invalidateIntegrationSecrets = useInvalidateIntegrationSecrets();

  const { canEditIntegrationSecrets, canViewIntegrationSecrets } =
    useMarketplacePermissions();

  const onChangeSetupAssistantConfig = useCallback(
    async (
      pluginApiName: string,
      assistantConfig: any,
      templateAssociations?: Array<any>
    ) => {
      const { newConfig, actionsToLink } = await processConfig(
        pluginApiName,
        assistantConfig,
        businessPluginAppsByApiName,
        canEditIntegrationSecrets
      );

      await PluginService.updateBusinessPluginApp(pluginApiName, {
        config: newConfig,
      });

      await performAssociationLink(
        pluginApiName,
        actionsToLink,
        templateAssociations
      );

      await refetch(pluginApiName);
    },
    [businessPluginAppsByApiName, refetch, canEditIntegrationSecrets]
  );

  const onImportKZNFileStep = useCallback(
    async (pluginApiName: string, etag?: string) => {
      const businessPluginApp = businessPluginAppsByApiName[pluginApiName];

      let existingJSONConfig = businessPluginApp?.config;

      if (!existingJSONConfig) {
        existingJSONConfig = {};
      }

      const newConfig = {
        ...existingJSONConfig,
        __kizen_schema_import_etag:
          etag || existingJSONConfig.__kizen_schema_import_etag,
        __kizen_schema_import_skipped: !etag,
        __kizen_schema_import_ts: etag
          ? Date.now()
          : existingJSONConfig.__kizen_schema_import_ts,
      };

      await PluginService.updateBusinessPluginApp(pluginApiName, {
        config: newConfig,
      });

      await refetch();
    },
    [businessPluginAppsByApiName, refetch]
  );

  const maybeShowSetupAssistant = useCallback(
    (
      pluginApiName: string,
      triggeredByEnable: boolean,
      marketplacePlugin: MarketplacePlugin
    ) => {
      const currentSetupAssistantConfig =
        marketplacePlugin?.base_config?.setup_assistant;

      const hasSetupAssistant = Boolean(currentSetupAssistantConfig);

      const { showBlockingImportOnEnable } = getKZNFileFlagsFromConfig(
        marketplacePlugin,
        businessPluginAppsByApiName[pluginApiName]?.config
      );

      if (showBlockingImportOnEnable && triggeredByEnable) {
        setSetupAssistantPending(true);
        setSetupAssistantConfig(marketplacePlugin);
        return true;
      }

      if (hasSetupAssistant) {
        const configHash = getHash(JSON.stringify(currentSetupAssistantConfig));

        let existingJSONConfig =
          businessPluginAppsByApiName[pluginApiName]?.config;

        if (!existingJSONConfig) {
          existingJSONConfig = {};
        }

        if (
          existingJSONConfig.__kizen_setup_assistant_hash &&
          existingJSONConfig.__kizen_setup_assistant_hash === configHash &&
          triggeredByEnable
        ) {
          setSetupAssistantConfig(null);
          setSetupAssistantPending(false);
          return false;
        }

        setSetupAssistantPending(true);
        setSetupAssistantConfig(marketplacePlugin);
        return true;
      }

      return false;
    },
    [businessPluginAppsByApiName]
  );

  const onChangeEnabled = useCallback(
    async (
      pluginApiName: string,
      enabled: boolean,
      plugin: MarketplacePlugin
    ) => {
      let succeeded = true;

      try {
        setPendingChange({ apiName: pluginApiName, enabled });
        const existingConfig = businessPluginAppsByApiName[pluginApiName];

        if (!existingConfig) {
          const template = plugin?.config_template ?? {};

          await PluginService.createBusinessPluginApp({
            disabled: false,
            plugin_app_api_name: pluginApiName,
            config: template,
          });
        } else {
          await PluginService.updateBusinessPluginApp(pluginApiName, {
            disabled: !enabled,
          });
        }

        await refetch();
        // Installing/uninstalling a plugin creates/removes its integration secrets
        invalidateIntegrationSecrets();

        showToast({
          message: t('{{name}} {{action}} successfully', {
            action: enabled ? 'installed' : 'uninstalled',
            name: plugin.name,
          }),
          variant: toastVariant.SUCCESS,
        });
      } catch (ex: any) {
        showToast({
          message: `${t('An error occurred while {{action}} the app', {
            action: enabled ? 'installing' : 'uninstalling',
          })}${ex?.message ? `: ${ex.message}` : ''}`,
          variant: toastVariant.FAILURE,
        });

        monitoringExceptionHelper(ex);
        succeeded = false;
      } finally {
        const hasSecrets =
          succeeded && (plugin?.base_config?.secrets?.length ?? 0) > 0;
        const ownedSecretNames = getOwnedSecretNames(
          plugin?.base_config?.setup_assistant,
          plugin?.base_config?.user_setup_assistant
        );

        if (enabled && succeeded) {
          maybeShowSetupAssistant(pluginApiName, true, plugin);
        }

        if (hasSecrets && enabled && canEditIntegrationSecrets) {
          try {
            const { hasEmptySecrets, emptySecrets } = await getEmptySecrets(
              plugin.name,
              pluginApiName,
              ownedSecretNames
            );

            if (hasEmptySecrets) {
              setSecretsToEdit(
                emptySecrets.map((s: any) => {
                  return {
                    id: s.id,
                    data: s,
                    action: 'edit',
                  };
                })
              );
            } else {
              setSecretsToEdit([]);
            }
          } catch (ex) {
            monitoringExceptionHelper(ex);
          }
        } else if (hasSecrets && enabled && !canEditIntegrationSecrets) {
          // If the user has view permission to secrets, we can check if they exist
          if (canViewIntegrationSecrets) {
            try {
              const { hasEmptySecrets } = await getEmptySecrets(
                plugin.name,
                pluginApiName,
                ownedSecretNames
              );

              if (hasEmptySecrets) {
                noPermissionModal.show();
              }
            } catch (ex) {
              monitoringExceptionHelper(ex);
            }
          } else {
            //  If no integration secrets permission, we can't check if they exist, so need to show the modal always
            // (this shouldn't be possible with the current permission configuration at time of development)
            noPermissionModal.show();
          }
        }

        setPendingChange({ apiName: '', enabled: false });
      }

      return succeeded;
    },
    [
      refetch,
      invalidateIntegrationSecrets,
      businessPluginAppsByApiName,
      canEditIntegrationSecrets,
      maybeShowSetupAssistant,
      noPermissionModal,
      canViewIntegrationSecrets,
      showToast,
      t,
    ]
  );

  const handleCloseSecretsInlineModal = useCallback(() => {
    setSecretsToEdit((prev: Array<any>) => {
      const newSecrets = [...prev];
      newSecrets.shift();
      return newSecrets;
    });
  }, []);

  const handleCloseSetupAssistant = useCallback(() => {
    setSetupAssistantPending(false);
    setSetupAssistantConfig(null);
  }, []);

  return {
    onChangeEnabled,
    secretsToEdit,
    setupAssistantConfig,
    pendingChange,
    noPermissionModalProps,
    setupAssistantPending,
    handleCloseSecretsInlineModal,
    handleCloseSetupAssistant,
    canEditIntegrationSecrets,
    canViewIntegrationSecrets,
    onChangeSetupAssistantConfig,
    onImportKZNFileStep,
  };
};
